import {
  ArrowDownTrayIcon,
  ClockIcon,
  HeartIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React from 'react';
import {
  DetailField,
  StatusPill
} from '../../../components/detail/detail-parts';
import { DownloadLabel } from '../../../components/detail/DownloadLabel';
import { Button } from '../../../components/ui/button';
import {
  DONATION_NON_REFUNDABLE,
  DONATION_TONE
} from '../../../constants/donation.const';
import { receiptDocumentPath } from '../../../api/receipt.api';
import { useDownloadInFlight } from '../../../hooks/useDownloadInFlight';
import type {
  DonationStatus,
  TransactionDonation
} from '../../../types/donation.types';
import { taxStatusLabel } from '../../../utils/donation-format';
import { formatCurrency } from '../../../utils/fiat-currency.util';

interface DonationCardProps {
  donation: TransactionDonation;
  onDownloadReceipt: () => void;
}

/** A failed donation took nothing from the donor, so its pill is neutral, never an error. */
const PILL_BY_STATUS: Record<DonationStatus, string> = {
  paid: 'paid',
  processing: 'processing',
  failed: 'not_charged'
};

const CardHeader = ({
  status,
  children
}: React.PropsWithChildren<{ status: DonationStatus }>) => (
  <div className="flex items-start gap-3">
    <span
      className={clsx(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
        DONATION_TONE.iconTile
      )}
    >
      <HeartIcon className="h-5 w-5" aria-hidden="true" />
    </span>
    <div className="min-w-0 flex-1">
      <p
        className={clsx(
          'text-xs font-semibold uppercase tracking-widest',
          DONATION_TONE.text
        )}
      >
        Donation
      </p>
      {children}
    </div>
    <StatusPill status={PILL_BY_STATUS[status]} className="text-xs" />
  </div>
);

/**
 * The donation a checkout carried: its charity, reference and receipt when paid; a held receipt
 * while processing; and, when its charge failed, that the donor was not charged for it.
 */
export const DonationCard: React.FC<DonationCardProps> = ({
  donation,
  onDownloadReceipt
}) => {
  const { charity } = donation;
  const preparing = useDownloadInFlight(
    receiptDocumentPath.donation(donation.donation_ref)
  );

  if (donation.status === 'processing') {
    return (
      <section
        aria-label="Donation"
        className={clsx('rounded-3xl border p-5 shadow-sm', DONATION_TONE.card)}
      >
        <CardHeader status="processing" />
        <DetailField
          className="mt-2"
          label="Recipient"
          value={charity.name}
          valueClassName="text-base font-semibold"
        />
        <div className="grid grid-cols-2 gap-4">
          <DetailField
            label="Amount"
            value={formatCurrency(donation.amount, donation.currency)}
            valueClassName={clsx('text-base font-bold', DONATION_TONE.text)}
          />
          <DetailField label="Donation ref" value={donation.donation_ref} />
        </div>
        <div className="mt-3 flex gap-3 rounded-2xl border border-orange-200 bg-white p-4">
          <ClockIcon
            className={clsx('h-5 w-5 shrink-0', DONATION_TONE.pendingText)}
            aria-hidden="true"
          />
          <p className="text-sm text-gray-800">
            Your purchase is complete. We're still processing your donation and
            will update this page once it's paid.
          </p>
        </div>
        <button
          type="button"
          disabled
          className="mt-3 flex w-full items-center gap-3 rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-3 text-sm text-gray-400"
        >
          <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
          Donation receipt available once paid
        </button>
      </section>
    );
  }

  const references = (
    <div className="mt-2">
      <DetailField
        label="Donation ref"
        value={donation.donation_ref}
        copyValue={donation.donation_ref}
      />
      {donation.transaction_id && (
        <DetailField
          label="Transaction ID"
          value={donation.transaction_id}
          copyValue={donation.transaction_id}
        />
      )}
    </div>
  );

  if (donation.status === 'failed') {
    return (
      <section
        aria-label="Donation"
        className={clsx('rounded-3xl border p-5 shadow-sm', DONATION_TONE.card)}
      >
        <CardHeader status="failed">
          <p className="text-base font-semibold text-gray-900">
            {charity.name}
          </p>
        </CardHeader>
        <div className="mt-3 flex gap-3 rounded-2xl border border-gray-200 bg-white p-4">
          <InformationCircleIcon
            className="h-5 w-5 shrink-0 text-gray-500"
            aria-hidden="true"
          />
          <p className="text-sm text-gray-800">
            Your donation of{' '}
            {formatCurrency(donation.amount, donation.currency)} to{' '}
            {charity.name} couldn't be processed. You were not charged for the
            donation.
          </p>
        </div>
        {references}
      </section>
    );
  }

  return (
    <section
      aria-label="Donation"
      className={clsx('rounded-3xl border p-5 shadow-sm', DONATION_TONE.card)}
    >
      <CardHeader status="paid">
        <p className="text-base font-semibold text-gray-900">{charity.name}</p>
      </CardHeader>
      <p className="mt-3 text-xs text-gray-600">
        {taxStatusLabel(charity.tax_deductible)}
        {charity.registration_no && ` · Reg. no. ${charity.registration_no}`}
      </p>
      {references}
      <Button
        type="button"
        variant="outline"
        onClick={onDownloadReceipt}
        disabled={preparing}
        aria-busy={preparing}
        className={clsx(
          'mt-3 h-12 w-full rounded-xl text-sm font-semibold',
          DONATION_TONE.outlineButton
        )}
      >
        <DownloadLabel pending={preparing}>
          Download donation receipt (PDF)
        </DownloadLabel>
      </Button>
      {!charity.tax_deductible && (
        <p className="mt-3 text-xs text-gray-600">
          The receipt is for your records. This charity isn't approved for tax
          deduction, so it can't be used for tax relief.
        </p>
      )}
      <p className="mt-3 text-xs text-gray-600">
        <span className="font-semibold text-gray-900">
          {DONATION_NON_REFUNDABLE}.
        </span>{' '}
        Shows as a separate charge on your statement.
      </p>
    </section>
  );
};
